import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Palette,
  CopyPlus,
  Trash2,
  RotateCw,
  Activity,
  Droplets,
  Sparkles,
  ChevronUp,
  ChevronDown,
  CircleSlash,
  Cloud,
  ArrowRight,
  ArrowUp,
  Maximize2
} from 'lucide-react';

const ANIMATION_OPTIONS = [
  { id: 'none', label: 'None', icon: CircleSlash, previewClass: '' },
  { id: 'fadeIn', label: 'Fade', icon: Cloud, previewClass: 'preview-animation-fade' },
  { id: 'slideIn', label: 'Slide', icon: ArrowRight, previewClass: 'preview-animation-slideIn' },
  { id: 'scale', label: 'Rise', icon: ArrowUp, previewClass: 'preview-animation-rise' },
  { id: 'pop', label: 'Pop', icon: Maximize2, previewClass: 'preview-animation-pop' },
  { id: 'bounce', label: 'Bounce', icon: Activity, previewClass: 'preview-animation-pulse' },
  { id: 'rotate', label: 'Rotate', icon: RotateCw, previewClass: 'preview-animation-rotate' },
];
import { motion, AnimatePresence } from 'motion/react';
import Corner from "../../../imports/Corner";

import Opacity from "../../../imports/Opacity";
import { UniversalColorPicker } from '../shared/UniversalColorPicker';

export interface ShapeSettings {
  fill: import('../../types/design').FillSettings;
  stroke: {
    enabled: boolean;
    color: string;
    width: number;
    style: 'solid' | 'dashed' | 'dotted';
  };
  cornerRadius: number;
  rotation: number;
  opacity: number;
  blur: number;
  animation?: import('../../types/design').AnimationSettings;
}

interface ShapePropertiesPanelProps {
  selectedShapeId: string | null;
  settings: ShapeSettings;
  dimensions?: { left: number; top: number; width: number; height: number; rotation: number };
  onSettingsChange: (settings: Partial<ShapeSettings> & { rotation?: number }, commit?: boolean) => void;
  onDimensionsChange?: (dimensions: Partial<{ left: number; top: number; width: number; height: number; rotation: number }>) => void;
  onDimensionsCommit?: () => void;
  onDuplicate?: (id: string) => void;
  onDelete?: (id: string) => void;
  onClose: () => void;
}

const PropertyInput: React.FC<{
  label?: string;
  value: number;
  onChange: (val: number) => void;
  width?: string;
  unit?: string;
  step?: number;
  min?: number;
  onBlur?: () => void;
}> = ({ label, value, onChange, width = "w-9", unit, step = 1, min, onBlur }) => (
  <div className="flex items-center h-7 px-2 rounded-lg bg-gray-50 border border-transparent hover:border-gray-200 focus-within:border-[#30A84B] focus-within:bg-white focus-within:shadow-[0_2px_4px_rgba(0,0,0,0.02)] transition-all group relative">
    {label && (
      <span className="text-[9px] font-black text-gray-400 mr-1.5 select-none uppercase tracking-widest group-focus-within:text-[#30A84B]">
        {label}
      </span>
    )}
    <input
      type="number"
      value={typeof value === 'number' ? (step < 1 ? value : Math.round(value)) : 0}
      onChange={(e) => {
        const val = parseFloat(e.target.value);
        if (!isNaN(val)) onChange(val);
      }}
      onBlur={() => onBlur?.()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onBlur?.();
      }}
      className={`${width} bg-transparent text-[11px] font-bold text-gray-700 outline-none text-left [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none pl-0.5`}
      step={step}
      min={min}
    />
    {unit && <span className="text-[10px] text-gray-400 font-bold ml-0.5">{unit}</span>}
    <div className="flex flex-col ml-1 opacity-0 group-hover:opacity-100 transition-all duration-200">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onChange(value + step); }}
        className="hover:bg-gray-200 rounded-sm p-0.5 text-gray-400 hover:text-gray-900 transition-colors"
      >
        <ChevronUp className="w-2.5 h-2.5" />
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onChange(min !== undefined ? Math.max(min, value - step) : value - step); }}
        className="hover:bg-gray-200 rounded-sm p-0.5 text-gray-400 hover:text-gray-900 transition-colors"
      >
        <ChevronDown className="w-2.5 h-2.5" />
      </button>
    </div>
  </div>
);

export const ShapePropertiesPanel: React.FC<ShapePropertiesPanelProps> = ({
  selectedShapeId,
  settings,
  dimensions,
  onSettingsChange,
  onDimensionsChange,
  onDimensionsCommit,
  onDuplicate,
  onDelete,
}) => {
  const [activePopover, setActivePopover] = useState<'fill' | 'stroke' | 'width' | 'options' | null>(null);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const triggerRefs = {
    fill: useRef<HTMLButtonElement>(null),
    stroke: useRef<HTMLButtonElement>(null),
    width: useRef<HTMLButtonElement>(null),
    options: useRef<HTMLButtonElement>(null),
  };

  const [isAnimDropdownOpen, setIsAnimDropdownOpen] = useState(false);
  const [animDropdownPos, setAnimDropdownPos] = useState({ top: 0, left: 0 });
  const [hoveredAnim, setHoveredAnim] = useState<string | null>(null);
  const animTriggerRef = useRef<HTMLButtonElement>(null);

  const getCompleteSettings = (s: any): ShapeSettings => {
    if (!s) s = {};
    return {
      fill: {
        type: s.fill?.type ?? 'solid',
        enabled: s.fill?.enabled ?? true,
        color: s.fill?.color ?? '#1e293b',
        gradientStart: s.fill?.gradientStart ?? '#1e293b',
        gradientEnd: s.fill?.gradientEnd ?? '#ffffff',
        gradientAngle: s.fill?.gradientAngle ?? 0
      },
      stroke: {
        enabled: s.stroke?.enabled ?? false,
        color: s.stroke?.color ?? '#000000',
        width: s.stroke?.width ?? 2,
        style: s.stroke?.style ?? 'solid'
      },
      cornerRadius: s.cornerRadius ?? 0,
      rotation: s.rotation ?? 0,
      opacity: s.opacity ?? 1,
      blur: s.blur ?? 0,
      animation: {
        type: s.animation?.type ?? 'none',
        duration: s.animation?.duration ?? 1000,
        delay: s.animation?.delay ?? 0,
        easing: s.animation?.easing ?? 'ease',
        loop: s.animation?.loop ?? false
      }
    };
  };

  const [localSettings, setLocalSettings] = useState(getCompleteSettings(settings));

  useEffect(() => {
    setLocalSettings(getCompleteSettings(settings));
  }, [settings]);

  useLayoutEffect(() => {
    if (activePopover) {
      const trigger = triggerRefs[activePopover as keyof typeof triggerRefs].current;
      if (trigger) {
        const rect = trigger.getBoundingClientRect();
        setPopoverPos({
          top: rect.bottom + 8,
          left: rect.left
        });
      }
    }
  }, [activePopover]);

  useLayoutEffect(() => {
    if (isAnimDropdownOpen && animTriggerRef.current) {
      const updatePosition = () => {
        if (animTriggerRef.current) {
          const rect = animTriggerRef.current.getBoundingClientRect();
          setAnimDropdownPos({
            top: rect.bottom + 8,
            left: Math.max(16, rect.left - 120)
          });
        }
      };
      updatePosition();
      window.addEventListener('resize', updatePosition);
      return () => window.removeEventListener('resize', updatePosition);
    }
  }, [isAnimDropdownOpen]);

  useEffect(() => {
    if (!activePopover && !isAnimDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const portal = document.getElementById('shape-popover-portal');
      const animPortal = document.getElementById('anim-dropdown-portal');
      const isTrigger = Object.values(triggerRefs).some(ref => ref.current?.contains(e.target as Node));
      const isAnimTrigger = animTriggerRef.current?.contains(e.target as Node);

      if (portal && !portal.contains(e.target as Node) && !isTrigger) {
        setActivePopover(null);
      }
      if (animPortal && !animPortal.contains(e.target as Node) && !isAnimTrigger) {
        setIsAnimDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activePopover, isAnimDropdownOpen]);

  const updateSetting = (key: keyof ShapeSettings, value: any, commit = false) => {
    const newSettings = { ...localSettings, [key]: value };
    setLocalSettings(newSettings);
    onSettingsChange({ [key]: value }, commit);
  };

  const updateNestedSetting = (parent: 'fill' | 'stroke' | 'animation', key: string, value: any, commit = false) => {
    const currentParent = (localSettings[parent] as any) || {};
    const newParent = { ...currentParent, [key]: value };

    // Auto-enable stroke if width or color is changed
    if (parent === 'stroke' && (key === 'width' || key === 'color')) {
      newParent.enabled = true;
    }

    updateSetting(parent, newParent, commit);
  };

  const updateNestedSettings = (parent: 'fill' | 'stroke' | 'animation', updates: Record<string, any>, commit = false) => {
    const currentParent = (localSettings[parent] as any) || {};
    const newParent = { ...currentParent, ...updates };

    if (parent === 'stroke' && ('width' in updates || 'color' in updates)) {
      newParent.enabled = true;
    }

    updateSetting(parent, newParent, commit);
  };

  if (!selectedShapeId) return null;

  const updateDimension = (key: string, value: number) => {
    if (onDimensionsChange) {
      onDimensionsChange({ [key]: value });
    }
  };

  return (
    <div className="flex flex-col gap-1.5 p-0.5" onMouseDown={(e) => e.stopPropagation()}>
      {/* Row 1: Transform & Actions */}
      <div className="flex items-center h-8 px-1 gap-0.5">
        {/* Label */}
        <div className="px-2 text-[10px] font-black text-[#30A84B] uppercase tracking-widest border-r border-gray-100 mr-1 select-none whitespace-nowrap">
          Shape
        </div>

        {/* Position Section */}
        {dimensions && (
          <div className="flex items-center gap-1.5 px-2 border-r border-gray-100 mr-1" title="Position">
            <div className="flex items-center gap-1">
              <PropertyInput
                label="X"
                value={dimensions.left}
                onChange={(val) => updateDimension('left', val)}
                onBlur={onDimensionsCommit}
              />
              <PropertyInput
                label="Y"
                value={dimensions.top}
                onChange={(val) => updateDimension('top', val)}
                onBlur={onDimensionsCommit}
              />
            </div>
          </div>
        )}

        {/* Size Section */}
        {dimensions && (
          <div className="flex items-center gap-1.5 px-2 border-r border-gray-100 mr-1" title="Size">
            <Maximize2 className="w-5 h-5 text-gray-400" />
            <div className="flex items-center gap-1">
              <PropertyInput
                label="W"
                value={dimensions.width}
                onChange={(val) => updateDimension('width', val)}
                onBlur={onDimensionsCommit}
                width="w-12"
              />
              <PropertyInput
                label="H"
                value={dimensions.height}
                onChange={(val) => updateDimension('height', val)}
                onBlur={onDimensionsCommit}
                width="w-12"
              />
            </div>
          </div>
        )}

        {/* Rotation Section */}
        {dimensions && (
          <div className="flex items-center gap-1.5 px-2 border-r border-gray-100 mr-1" title="Rotation">
            <RotateCw className="w-5 h-5 text-gray-400" />
            <PropertyInput
              label=""
              value={dimensions.rotation || 0}
              onChange={(val) => updateDimension('rotation', val)}
              onBlur={onDimensionsCommit}
              unit="°"
              width="w-10"
            />
          </div>
        )}

        {/* Actions Button (Duplicate/Delete) */}
        <div className="flex items-center ml-auto pl-1 gap-0.5 border-l border-gray-100">
          <button
            type="button"
            onClick={() => onDuplicate?.(selectedShapeId)}
            className="p-1.5 rounded-md text-gray-400 hover:text-green-600 hover:bg-green-50 transition-all"
            title="Duplicate"
          >
            <CopyPlus className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => onDelete?.(selectedShapeId)}
            className="p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all"
            title="Delete layer"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Divider */}
      <div className="h-px bg-gray-100/60 mx-2" />

      {/* Row 2: Styling */}
      <div className="flex items-center h-9 px-1 gap-0.5">
        {/* Fill Section */}
        <div className="flex items-center gap-2 px-2 border-r border-gray-100 mr-1" title="Fill Color">
          <div className="relative">
              <UniversalColorPicker
                value={localSettings.fill}
                onChange={(fill, commit) => updateSetting('fill', fill, commit)}
                label="Fill Color"
                id="shape-fill-popover"
              />

          </div>
          <div className={`flex items-center transition-opacity ${!localSettings.fill?.enabled ? 'opacity-40 grayscale pointer-events-none' : ''}`}>
            <span className="text-[11px] font-mono font-bold text-gray-500 uppercase">
              {(localSettings.fill?.color ?? '#000000').replace('#', '')}
            </span>
          </div>
        </div>

        {/* Stroke Section */}
        <div className="flex items-center gap-2 px-2 border-r border-gray-100 mr-1" title="Stroke settings">
          <div className="relative">
            <button
              ref={triggerRefs.stroke}
              type="button"
              onClick={() => setActivePopover(activePopover === 'stroke' ? null : 'stroke')}
              className="flex items-center gap-1.5 bg-gray-50/50 p-1 rounded-full border border-gray-100 hover:bg-gray-100 transition-colors"
            >
              <div
                className="w-5 h-5 rounded-full border-2 border-white shadow-sm flex items-center justify-center overflow-hidden"
                style={{
                  backgroundColor: localSettings.stroke?.enabled ? localSettings.stroke.color : 'transparent',
                  backgroundImage: !localSettings.stroke?.enabled ? 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc), linear-gradient(45deg, #ccc 25%, #fff 25%, #fff 75%, #ccc 75%, #ccc)' : 'none',
                  backgroundSize: '4px 4px',
                  backgroundPosition: '0 0, 2px 2px'
                }}
              >
                {!localSettings.stroke?.enabled && <div className="w-full h-0.5 bg-red-500/80 rotate-45" />}
              </div>
              <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${activePopover === 'stroke' ? 'rotate-180' : ''}`} />
            </button>

            {activePopover === 'stroke' && createPortal(
              <AnimatePresence>
                <motion.div
                  id="shape-popover-portal"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  className="fixed w-[200px] bg-white border border-gray-200 shadow-[0_12px_40_rgba(0,0,0,0.12)] rounded-xl z-[10000] p-3 overflow-hidden"
                  style={{ top: `${popoverPos.top}px`, left: `${popoverPos.left}px` }}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Stroke Color</div>
                  <div className="grid grid-cols-5 gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => {
                        updateNestedSetting('stroke', 'enabled', false);
                        setActivePopover(null);
                      }}
                      className={`w-7 h-7 rounded-lg border border-gray-200 flex items-center justify-center bg-white transition-all hover:scale-110 active:scale-95 ${!localSettings.stroke?.enabled ? 'ring-2 ring-[#30A84B] ring-offset-1' : ''}`}
                      title="Transparent"
                    >
                      <div className="w-full h-0.5 bg-red-500/80 rotate-45" />
                    </button>
                    {['#000000', '#ffffff', '#f87171', '#60a5fa', '#34d399', '#fbbf24', '#a78bfa', '#f472b6', '#94a3b8'].map(c => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => {
                          updateNestedSettings('stroke', { color: c, enabled: true });
                          setActivePopover(null);
                        }}
                        className={`w-7 h-7 rounded-lg border border-gray-100 shadow-sm transition-transform hover:scale-110 active:scale-95 ${localSettings.stroke?.enabled && localSettings.stroke.color === c ? 'ring-2 ring-[#30A84B] ring-offset-1' : ''}`}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>

                  <div className="pt-3 border-t border-gray-50 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="relative w-6 h-6 rounded-lg overflow-hidden border border-gray-200 shadow-sm group">
                        <input
                          type="color"
                          value={localSettings.stroke?.color ?? '#000000'}
                          onChange={(e) => updateNestedSettings('stroke', { color: e.target.value, enabled: true })}
                          className="absolute inset-[-4px] w-[calc(100%+8px)] h-[calc(100%+8px)] cursor-pointer bg-transparent border-none p-0"
                        />
                      </div>
                      <span className="text-[11px] font-mono font-bold text-gray-500 uppercase">
                        {(localSettings.stroke?.color ?? '#000000').replace('#', '')}
                      </span>
                    </div>
                    <Palette className="w-5 h-5 text-gray-400" />
                  </div>
                </motion.div>
              </AnimatePresence>,
              document.body
            )}
          </div>
          <div className={`flex items-center gap-2 transition-opacity ${!localSettings.stroke?.enabled ? 'opacity-40 grayscale pointer-events-none' : ''}`}>
            <span className="text-[11px] font-mono font-bold text-gray-500 uppercase">
              {(localSettings.stroke?.color ?? '#000000').replace('#', '')}
            </span>
            <div className="flex items-center ml-1">
              <PropertyInput
                value={localSettings.stroke?.width ?? 0}
                onChange={(val) => updateNestedSetting('stroke', 'width', val)}
                width="w-7"
                min={0}
              />
            </div>
          </div>
        </div>

        {/* Radius Section */}
        <div className="flex items-center gap-2 px-2 border-r border-gray-100 mr-1" title="Corner Radius">
          <div className="flex items-center group">
            <div className="w-5 h-5 text-gray-400 group-focus-within:text-[#30A84B] transition-colors [--fill-0:currentColor] [--stroke-0:currentColor] mr-1.5">
              <Corner />
            </div>
            <PropertyInput
              value={localSettings.cornerRadius ?? 0}
              onChange={(val) => updateSetting('cornerRadius', val)}
              width="w-7"
              min={0}
            />
          </div>
          <input
            type="range"
            min="0"
            max="100"
            value={localSettings.cornerRadius ?? 0}
            onChange={(e) => updateSetting('cornerRadius', parseInt(e.target.value), false)}
            onMouseUp={(e) => updateSetting('cornerRadius', parseInt((e.target as HTMLInputElement).value), true)}
            onBlur={(e) => updateSetting('cornerRadius', parseInt((e.target as HTMLInputElement).value), true)}
            className="w-16 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#30A84B] hover:accent-green-600 transition-all"
          />
        </div>

        {/* Opacity Section */}
        <div className="flex items-center gap-2 px-2 border-r border-gray-100 mr-1" title="Opacity">
          <div className="flex items-center gap-1 h-7 px-1 rounded-md">
            <div className="w-5 h-5 text-gray-400">
              <Opacity />
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={localSettings.opacity ?? 1}
              onChange={(e) => updateSetting('opacity', parseFloat(e.target.value), false)}
              onMouseUp={(e) => updateSetting('opacity', parseFloat((e.target as HTMLInputElement).value), true)}
              onBlur={(e) => updateSetting('opacity', parseFloat((e.target as HTMLInputElement).value), true)}
              className="w-16 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-500 hover:accent-green-600 transition-all"
            />
          </div>
          <span className="text-[11px] font-bold text-gray-500 w-8 text-right">
            {Math.round((localSettings.opacity ?? 1) * 100)}%
          </span>
        </div>

        {/* Blur Section */}
        <div className="flex items-center gap-2 px-2 border-r border-gray-100 mr-1" title="Blur">
          <div className="flex items-center group">
            <Droplets className="w-5 h-5 text-gray-400 group-focus-within:text-[#30A84B] transition-colors mr-1.5" />
            <PropertyInput
              value={localSettings.blur ?? 0}
              onChange={(val) => updateSetting('blur', val)}
              width="w-7"
              min={0}
            />
          </div>
          <input
            type="range"
            min="0"
            max="100"
            value={localSettings.blur ?? 0}
            onChange={(e) => updateSetting('blur', parseInt(e.target.value), false)}
            onMouseUp={(e) => updateSetting('blur', parseInt((e.target as HTMLInputElement).value), true)}
            onBlur={(e) => updateSetting('blur', parseInt((e.target as HTMLInputElement).value), true)}
            className="w-16 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#30A84B] hover:accent-green-600 transition-all"
          />
        </div>

        {/* Animation Selector */}
        <div className="flex items-center gap-2 px-2 border-r border-gray-100 mr-1" title="Shape Animation">
          <div className="relative">
            <button
              ref={animTriggerRef}
              type="button"
              onClick={() => setIsAnimDropdownOpen(!isAnimDropdownOpen)}
              className="flex items-center gap-1.5 h-7 px-2 rounded-md hover:bg-gray-100 transition-colors group"
            >
              <Sparkles className={`w-5 h-5 transition-colors ${localSettings.animation?.type && localSettings.animation.type !== 'none' ? 'text-[#30A84B]' : 'text-gray-400 group-hover:text-[#30A84B]'}`} />
              <span className="text-[12px] font-bold text-gray-700 min-w-[40px] text-left uppercase tracking-tighter">
                {ANIMATION_OPTIONS.find(a => a.id === (localSettings.animation?.type || 'none'))?.label || 'Static'}
              </span>
              <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${isAnimDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {createPortal(
              <AnimatePresence>
                {isAnimDropdownOpen && (
                  <motion.div
                    id="anim-dropdown-portal"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    className="fixed w-[260px] bg-white border border-gray-200 shadow-[0_12px_40px_rgba(0,0,0,0.12)] rounded-2xl z-[9999] overflow-hidden origin-top-left"
                    style={{ top: `${animDropdownPos.top}px`, left: `${animDropdownPos.left}px`, pointerEvents: 'auto' }}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    {/* Refined Header */}
                    <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between bg-gray-50/30">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em]">Motion Presets</span>
                      <div className="flex items-center gap-1.5">
                        <div className="w-1 h-1 rounded-full bg-[#30A84B]" />
                        <span className="text-[9px] font-medium text-gray-400">v1.2</span>
                      </div>
                    </div>

                    {/* Enhanced Grid */}
                    <div className="p-2 grid grid-cols-2 gap-2 max-h-[380px] overflow-y-auto scrollbar-none">
                      {ANIMATION_OPTIONS.map((anim) => (
                        <button
                          key={anim.id}
                          type="button"
                          onClick={() => {
                            updateNestedSetting('animation', 'type', anim.id);
                            setIsAnimDropdownOpen(false);
                          }}
                          onMouseEnter={() => setHoveredAnim(anim.id)}
                          onMouseLeave={() => setHoveredAnim(null)}
                          className={`group relative flex flex-col items-center justify-center gap-2 p-3.5 rounded-xl border transition-all duration-200 ${localSettings.animation?.type === anim.id
                            ? 'border-[#30A84B] bg-[#30A84B]/[0.03] ring-1 ring-[#30A84B]/20'
                            : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                            }`}
                        >
                          {/* Selection Indicator */}
                          {localSettings.animation?.type === anim.id && (
                            <div className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-[#30A84B]" />
                          )}

                          {/* Icon Stage */}
                          <div className={`flex items-center justify-center w-11 h-11 rounded-xl transition-all duration-300 ${localSettings.animation?.type === anim.id
                            ? 'bg-[#30A84B] text-white shadow-[0_4px_12px_rgba(48,168,75,0.25)]'
                            : 'bg-gray-50 text-gray-400 group-hover:bg-white group-hover:text-gray-600 group-hover:shadow-sm'
                            } ${hoveredAnim === anim.id ? anim.previewClass : ''}`}
                            style={{ '--preview-duration': '0.7s', '--preview-iteration': 'infinite' } as any}
                          >
                            <anim.icon className="w-[22px] h-[22px] stroke-[1.8]" />
                          </div>

                          <span className={`text-[11px] font-bold tracking-tight transition-colors ${localSettings.animation?.type === anim.id ? 'text-[#30A84B]' : 'text-gray-500 group-hover:text-gray-900'
                            }`}>
                            {anim.label}
                          </span>
                        </button>
                      ))}
                    </div>

                    {/* Clean Subtle Footer */}
                    <div className="p-3 border-t border-gray-50 flex items-center justify-center">
                      <span className="text-[9px] font-medium text-gray-300 italic">Select to apply effect</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>,
              document.body
            )}
          </div>

          {localSettings.animation?.type && localSettings.animation.type !== 'none' && (
            <PropertyInput
              value={localSettings.animation.duration || 1000}
              onChange={(val) => updateNestedSetting('animation', 'duration', val)}
              width="w-10"
              unit="MS"
              step={100}
              min={0}
            />
          )}
        </div>
      </div>
    </div>
  );
};

